import * as React from 'react';
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { IUserResult, store } from 'Models/Store';
import axios from 'axios';
import { Navigate, useLocation } from 'react-router-dom';
import { SERVER_URL } from 'Constants';
import { LottieSpinner } from '../_HumanWritten/Lottie/LottieSpinner';

export interface AuthProps {
	/** If true then don't redirect on login check failure. */
	checkOnly?: boolean;
	/** The location that the component will redirect to on login check failure. */
	redirectLocation?: string;
	/** Callback on login check success. */
	onAfterSuccess?: () => void;
	/** Callback on login check failure. Note that if checkOnly is not set to true the user will still be redirected. */
	onAfterError?: () => void;
	/** The children to render when authenticated */
	children: React.ReactNode;
}

/**
 * This component handles the requirement for a user being logged in before they can access the child components.
 *
 * If the user is logged in then the this component will just render the child props.
 *
 * If the user is not flagged as logged in with Javascript then it will send a request to the server to see if they are
 * actually authenticated. If they are then the user is set as logged in, otherwise they are redirected to the login
 * page.
 */
const Auth = observer((props: AuthProps) => {
	const { checkOnly, redirectLocation, onAfterSuccess, onAfterError, children } = props;

	const location = useLocation();
	const [requestState, setRequestState] = useState<'pending' | 'error' | 'success'>('pending');

	useEffect(() => {
		const onSuccess = (userResult?: IUserResult) => {
			if (userResult) {
				store.setLoggedInUser(userResult);
			}
			setRequestState('success');
			onAfterSuccess?.();
		};

		const onError = () => {
			store.clearLoggedInUser();
			setRequestState('error');
			onAfterError?.();
		};

		// If we are already logged in then we don't need to check again
		if (store.loggedIn) {
			onSuccess();
			return;
		}

		// Otherwise send a request to the server to see if the token is valid
		axios
			.get(`${SERVER_URL}/api/account/me`)
			.then(({ data }) => onSuccess(data))
			.catch(onError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const redirect = redirectLocation ?? location.pathname + location.search;

	switch (requestState) {
		case 'pending':
			return <LottieSpinner />;
		case 'success':
			return <>{children}</>;
		case 'error':
		default:
			if (checkOnly) {
				return <>{children}</>;
			}
			return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
	}
});

export default Auth;
